import type { CargoLifecycleProjection } from '../services/cargoLifecycle/cargoLifecycleProjectionTypes';

/** Нормализация статуса маршрута для UI (как на бэкенде: IN_PROGRESS → в пути). */
export function normalizeRouteStatusForCargoUi(raw: string | null | undefined): string {
  const s = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (
    s === 'IN_TRANSIT' ||
    s === 'IN_PROGRESS' ||
    s === 'INPROGRESS' ||
    s === 'ON_WAY' ||
    s === 'ONWAY'
  ) {
    return 'IN_TRANSIT';
  }
  return s;
}

export function routeCountsAsInTransit(normalized: string): boolean {
  return normalized === 'IN_TRANSIT';
}

/** Краткая подпись статуса маршрута для строки аллокации в UI. */
export function routeStatusUiLabel(raw: string | null | undefined): string {
  const n = normalizeRouteStatusForCargoUi(raw);
  if (n === 'IN_TRANSIT') return 'В пути';
  if (n === 'COMPLETED') return 'Завершён';
  if (n === 'CREATED') return 'Создан';
  if (n === 'CANCELLED' || n === 'CANCELED') return 'Отменён';
  const s = String(raw || '').trim();
  return s || '—';
}

export function transportStatusTagColor(label: string): string {
  switch (label) {
    case 'Не распределён':
      return 'default';
    case 'Частично распределён':
      return 'gold';
    case 'Частично в пути':
      return 'orange';
    case 'В пути':
      return 'orange';
    case 'Распределён':
      return 'blue';
    case 'Доставлен':
      return 'green';
    case 'На складе':
      return 'purple';
    default:
      return 'default';
  }
}

export type CargoTransportDisplay = {
  label: string;
  inRoutes: number;
  inTransit: number;
  available: number;
  delivered?: number;
};

/**
 * CARGO-L3: transport display from lifecycle projection API (no local activeQuantity math).
 */
export function computeCargoTransportDisplayFromProjection(
  projection: CargoLifecycleProjection
): CargoTransportDisplay {
  const label = transportLabelFromProjection(projection);
  return {
    label,
    inRoutes: projection.inRoutesActive,
    inTransit: projection.inTransitActive,
    available: projection.available,
    delivered: projection.deliveredTotal,
  };
}

export function transportLabelFromProjection(projection: CargoLifecycleProjection): string {
  const eps = 1e-6;
  const {
    inRoutesActive,
    inTransitActive,
    available,
    quantity,
    deliveredStatus,
    businessStatus,
  } = projection;

  if (businessStatus === 'on_warehouse' && inRoutesActive <= eps) {
    return 'На складе';
  }
  if (businessStatus === 'delivered' || deliveredStatus === 'full') {
    if (inRoutesActive <= eps) return 'Доставлен';
  }
  if (inRoutesActive <= eps) {
    return 'Не распределён';
  }
  if (inRoutesActive < quantity - eps) {
    if (inTransitActive > eps) return 'Частично в пути';
    return 'Частично распределён';
  }
  if (Math.abs(inTransitActive - quantity) < eps && quantity > eps) {
    return 'В пути';
  }
  if (inTransitActive > eps) {
    return 'Частично в пути';
  }
  if (available <= eps && inRoutesActive >= quantity - eps) {
    return 'Распределён';
  }
  return 'Частично распределён';
}

/**
 * @deprecated CARGO-L3 — prefer computeCargoTransportDisplayFromProjection when projection UI is on.
 * Статус перевозки по аллокациям и статусам маршрутов (legacy allocatedQuantity math).
 */
export function computeCargoTransportDisplay(
  totalQty: number | null | undefined,
  byRoute: { routeId: number; amount: number }[],
  routeStatusById: Map<number, string>
): CargoTransportDisplay {
  const total = Math.max(0, Number(totalQty) || 0);
  let inRoutes = 0;
  let inTransit = 0;
  for (const row of byRoute) {
    const a = Math.max(0, Number(row.amount) || 0);
    if (a <= 0) continue;
    inRoutes += a;
    const st = normalizeRouteStatusForCargoUi(routeStatusById.get(row.routeId));
    if (routeCountsAsInTransit(st)) {
      inTransit += a;
    }
  }
  const available = Math.max(0, total - inRoutes);

  const eps = 1e-6;
  if (inRoutes <= eps) {
    return { label: 'Не распределён', inRoutes: 0, inTransit: 0, available: total };
  }
  if (inRoutes < total - eps) {
    if (inTransit > eps) {
      return { label: 'Частично в пути', inRoutes, inTransit, available };
    }
    return { label: 'Частично распределён', inRoutes, inTransit: 0, available };
  }
  if (Math.abs(inTransit - total) < eps && total > eps) {
    return { label: 'В пути', inRoutes, inTransit, available };
  }
  if (inTransit > eps) {
    return { label: 'Частично в пути', inRoutes, inTransit, available };
  }
  return { label: 'Распределён', inRoutes, inTransit: 0, available };
}
